import React, { useEffect, useMemo, useRef, useState } from 'react';
import { categories, collections, filterWork, work } from './bodyshop/work';
import './bodyshop/BodyshopHood.css';
import './bodyshop/BodyshopWork.css';

const QUOTE = '/bodyshop#bh-contact';

export default function BodyshopWork() {
  const [category, setCategory] = useState('All work');
  const [selected, setSelected] = useState(null);
  const [collection, setCollection] = useState('all');
  const [limit, setLimit] = useState(12);
  const [playing, setPlaying] = useState(false);
  const [loadedImage, setLoadedImage] = useState(null);
  const dialog = useRef(null);
  const trigger = useRef(null);
  const touchStart = useRef(null);
  const visible = useMemo(() => filterWork(category, collection), [category, collection]);
  const active = visible.find(photo => photo.id === selected);
  const collectionTitle = collections.find(group => group.id === collection)?.title || 'All collections';
  const chooseCollection = id => { setCollection(id); setCategory('All work'); setLimit(12); };

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Our Work | Paintwork Gallery | Bodyshop Hood';
    document.body.classList.add('bodyshop-active');
    const existingMeta = document.querySelector('meta[name="description"]');
    const meta = existingMeta || document.createElement('meta');
    const previousDescription = meta.getAttribute('content');
    if (!existingMeta) { meta.name = 'description'; document.head.appendChild(meta); }
    meta.content = 'Explore paintwork by the Bodyshop Hood painter: vehicle panels, bumpers, bonnets and preparation. Send photos of your car for a tailored repair estimate.';
    return () => {
      document.title = previousTitle;
      document.body.classList.remove('bodyshop-active');
      if (!existingMeta) meta.remove();
      else if (previousDescription === null) meta.removeAttribute('content');
      else meta.content = previousDescription;
    };
  }, []);

  const isOpen = Boolean(active);
  useEffect(() => {
    const modal = dialog.current;
    if (!isOpen) { if (modal?.open) modal.close(); return undefined; }
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (modal && !modal.open) modal.showModal();
    return () => { document.body.style.overflow = overflow; if (modal?.open) modal.close(); };
  }, [isOpen]);

  const changePhoto = (direction) => {
    if (!visible.length) return;
    const index = visible.findIndex(photo => photo.id === selected);
    setSelected(visible[(index + direction + visible.length) % visible.length].id);
  };
  const closePhoto = () => { setPlaying(false); setSelected(null); };
  useEffect(() => {
    if (!playing || !isOpen || visible.length < 2) return undefined;
    const timer = window.setTimeout(() => {
      const index = visible.findIndex(photo => photo.id === selected);
      setSelected(visible[(index + 1) % visible.length].id);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [playing, isOpen, selected, visible]);

  return (
    <main className="bh-page bh-work-page">
      <a className="bh-skip" href="#bw-gallery">Skip to gallery</a>
      <header className="bw-header">
        <a className="bh-brand" href="/bodyshop" aria-label="Bodyshop Hood home">BODYSHOP HOOD</a>
        <nav aria-label="Main navigation">
          <a href="/bodyshop">Home</a>
          <a href="/bodyshop/our-work" aria-current="page">Our work</a>
          <a className="bh-button" href={QUOTE}>Get a quote <span aria-hidden="true">↗</span></a>
        </nav>
      </header>

      <section className="bw-intro bh-container" aria-labelledby="bw-title">
        <div className="bw-intro-copy">
          <p className="bh-eyebrow">THE PAINTER’S PORTFOLIO</p>
          <h1 id="bw-title">Our work.<br /><em>In the detail.</em></h1>
          <p>A closer look at paintwork by our painter. From carefully prepared panels to the reflections in fresh paint.</p>
          <a className="bh-button" href="#bw-gallery">Explore the gallery <span aria-hidden="true">↓</span></a>
        </div>
        <figure className="bw-feature">
          <img src={work[1].image} alt="Grey hatchback with freshly painted door and rear quarter, still masked" fetchpriority="high" />
          <figcaption><span>01 / SELECTED PAINTWORK</span><span>Door &amp; rear quarter</span></figcaption>
        </figure>
      </section>

      <section className="bw-collection bh-container" id="bw-gallery" aria-labelledby="bw-gallery-title">
        <div className="bw-gallery-heading"><div><p className="bh-eyebrow">PAINTWORK, PANELS &amp; PREPARATION</p><h2 id="bw-gallery-title">Take a closer look.</h2></div><p>Tap a photo to see the detail.</p></div>
        <div className="bw-album-heading"><h3>Choose a collection.</h3><button type="button" className="bw-reset" onClick={() => chooseCollection('all')}>View all {work.length} photos</button></div>
        <div className="bw-albums" role="group" aria-label="Choose a work collection">
          {collections.filter(group => group.cover).map(group => <button type="button" key={group.id} aria-pressed={collection === group.id} onClick={() => chooseCollection(group.id)}>
            <img src={`/images/bodyshop-work/${group.cover}-640.webp`} alt="" loading="lazy" />
            <span>{group.title}<small>{work.filter(photo => photo.collection === group.id).length} photos</small></span>
          </button>)}
        </div>
        <label className="bw-collection-select">Collection<select value={collection} onChange={event => chooseCollection(event.target.value)}><option value="all">All collections</option>{collections.map(group => <option value={group.id} key={group.id}>{group.title}</option>)}</select></label>
        <div className="bw-filters" role="group" aria-label="Filter work photos">
          {categories.map(filter => <button type="button" key={filter} aria-pressed={category === filter} onClick={() => { setCategory(filter); setLimit(12); }}>{filter}</button>)}
        </div>
        <p className="bw-count" role="status">{visible.length} photos · {collectionTitle} · {category}</p>
        {!visible.length && <div className="bw-empty"><h3>{collection === 'classics' ? 'Classic-car work. Coming soon.' : 'No photos in this selection.'}</h3><p>{collection === 'classics' ? 'More from the painter’s portfolio will be added here.' : 'Try another category or explore all the work.'}</p><button className="bh-button" type="button" onClick={() => chooseCollection('all')}>Explore all work</button></div>}
        <div className="bw-grid">
          {visible.slice(0, limit).map((photo, index) => <figure className="bw-card" key={photo.id}>
            <button type="button" className="bw-photo-button" aria-label={`Enlarge: ${photo.title}`} onClick={event => { trigger.current = event.currentTarget; setSelected(photo.id); }}>
              <img src={photo.thumbnail} srcSet={`${photo.thumbnail} 640w, ${photo.image} ${photo.imageWidth}w`} sizes="(max-width: 600px) 90vw, (max-width: 1000px) 45vw, 30vw" alt={photo.description} loading="lazy" decoding="async" />
              <span className="bw-enlarge" aria-hidden="true">↗</span>
            </button>
            <figcaption><span className="bw-card-category">{String(index + 1).padStart(2, '0')} / {photo.category}</span><h3>{photo.title}</h3><p>{photo.description}</p></figcaption>
          </figure>)}
        </div>
        {limit < visible.length && <div className="bw-more"><p>Showing {Math.min(limit, visible.length)} of {visible.length} photos</p><button type="button" className="bh-button" onClick={() => setLimit(value => value + 12)}>Show more photos ↓</button></div>}
        <p className="bw-photo-note">Portfolio photographs of work by our painter. Some show preparation or fresh paint before final assembly. Backgrounds removed and identifying details obscured for privacy.</p>
      </section>

      <section className="bw-quote" aria-labelledby="bw-quote-title">
        <p className="bh-eyebrow">YOUR CAR COULD BE NEXT</p>
        <h2 id="bw-quote-title">Let’s talk<br />about your paintwork.</h2>
        <p>Private customers and car dealers welcome. Send photos of the damage and tell us what needs doing for a tailored estimate.</p>
        <a className="bh-button bh-button-solid" href={QUOTE}>Send your photos for a quote <span aria-hidden="true">↗</span></a>
        <a className="bw-call" href="tel:+447711804361">Or call 07711 804361</a>
      </section>
      <footer className="bh-footer"><nav aria-label="Footer navigation"><a href="/bodyshop">Home</a><a href="/bodyshop#bh-trade">Trade enquiries</a><a href={QUOTE}>Contact</a></nav><p>Bodyshop Hood · CHESSHOOD LTD · Company no. 16260623</p></footer>
      <div className="bw-mobile-quote"><a href={QUOTE}>Send photos for a quote <span aria-hidden="true">↗</span></a></div>

      <dialog ref={dialog} className="bw-lightbox" aria-labelledby="bw-photo-title" onCancel={closePhoto} onClose={() => { setPlaying(false); setSelected(null); trigger.current?.focus(); }} onClick={event => { if (event.target === event.currentTarget) closePhoto(); }} onKeyDown={event => { if (event.key === 'ArrowRight') { event.preventDefault(); changePhoto(1); } if (event.key === 'ArrowLeft') { event.preventDefault(); changePhoto(-1); } }}>
        {active && <div className="bw-lightbox-inner">
          <div className="bw-lightbox-top"><span>{visible.findIndex(photo => photo.id === active.id) + 1} / {visible.length}</span><button type="button" aria-pressed={playing} onClick={() => setPlaying(value => !value)} disabled={visible.length < 2}>{playing ? 'Pause slideshow' : 'Play slideshow'}</button><button type="button" onClick={closePhoto} autoFocus aria-label="Close photo">Close ×</button></div>
          <div className="bw-lightbox-image" onTouchStart={event => { touchStart.current = event.changedTouches[0].clientX; }} onTouchEnd={event => { if (touchStart.current === null) return; const distance = event.changedTouches[0].clientX - touchStart.current; if (Math.abs(distance) > 60) changePhoto(distance < 0 ? 1 : -1); touchStart.current = null; }}><img key={active.id} src={active.image} alt={active.description} onLoad={() => setLoadedImage(active.id)} />{loadedImage !== active.id && <span className="bw-loading" role="status">Loading photo…</span>}</div>
          <div className="bw-lightbox-caption"><div><h3 id="bw-photo-title">{active.title}</h3><p>{active.description}</p></div><div className="bw-lightbox-controls"><button type="button" onClick={() => changePhoto(-1)} aria-label="Previous photo">←</button><button type="button" onClick={() => changePhoto(1)} aria-label="Next photo">→</button></div></div>
          <div className="bw-thumbnails" role="group" aria-label="Choose a photo">{visible.map(photo => <button type="button" key={photo.id} aria-label={`View: ${photo.title}`} aria-pressed={active.id === photo.id} onClick={() => setSelected(photo.id)}><img src={photo.thumbnail} alt="" loading="lazy" /></button>)}</div>
          <a className="bw-lightbox-quote" href={QUOTE}>Have a similar repair? Send us your photos ↗</a>
        </div>}
      </dialog>
    </main>
  );
}

